import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '../stores/auth';
import { getCsrfToken } from './csrf';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const UNSAFE_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach CSRF header for mutating requests, matched
// against the CsrfGuard check on the backend (auth cookies run SameSite=None).
apiClient.interceptors.request.use(
  config => {
    if (config.method && UNSAFE_METHODS.has(config.method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const authStore = useAuthStore();
        await authStore.refreshAccessToken();
        return apiClient(originalRequest);
      } catch (refreshError) {
        const authStore = useAuthStore();
        authStore.clearAuthState();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  async login(credentials: { email: string; password: string }) {
    const response = await apiClient.post('/auth/login', {
      login: credentials.email,
      password: credentials.password,
    });
    return response.data;
  },

  async logout() {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  async refreshToken() {
    const response = await apiClient.post('/auth/refresh');
    return response.data;
  },
};

// Tenant API
export const tenantApi = {
  async create(data: any) {
    const response = await apiClient.post('/tenants', data);
    return response.data;
  },

  async findAll() {
    const response = await apiClient.get('/tenants');
    return response.data;
  },

  async findOne(id: string) {
    const response = await apiClient.get(`/tenants/${id}`);
    return response.data;
  },

  async update(id: string, data: any) {
    const response = await apiClient.put(`/tenants/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/tenants/${id}`);
    return response.data;
  },

  // Discount management
  async createDiscount(tenantId: string, data: any) {
    const response = await apiClient.post(`/tenants/${tenantId}/discounts`, data);
    return response.data;
  },

  async getDiscountHistory(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/discounts`);
    return response.data;
  },

  async getActiveDiscount(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/discounts/active`);
    return response.data;
  },

  async updateDiscount(tenantId: string, discountId: string, data: any) {
    const response = await apiClient.put(`/tenants/${tenantId}/discounts/${discountId}`, data);
    return response.data;
  },

  async removeDiscount(tenantId: string, discountId: string) {
    const response = await apiClient.delete(`/tenants/${tenantId}/discounts/${discountId}`);
    return response.data;
  },

  // Admin user password setup email
  async getAdminUserInfo(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/admin-user`);
    return response.data;
  },

  async resendPasswordSetupEmail(tenantId: string) {
    const response = await apiClient.post(`/tenants/${tenantId}/resend-setup-email`);
    return response.data;
  },

  async getFailedEmails(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/failed-emails`);
    return response.data;
  },

  async validateSubdomain(subdomain: string) {
    const response = await apiClient.get(`/tenants/validate-subdomain/${subdomain}`);
    return response.data;
  },

  async checkSubdomainAvailability(subdomain: string) {
    const response = await apiClient.get(`/tenants/check-subdomain/${subdomain}`);
    return response.data;
  },

  async activate(id: string) {
    const response = await apiClient.post(`/tenants/${id}/activate`);
    return response.data;
  },

  async suspend(id: string) {
    const response = await apiClient.post(`/tenants/${id}/suspend`);
    return response.data;
  },

  // Branch management
  async getBranches(tenantId: string) {
    const response = await apiClient.get(`/tenants/${tenantId}/branches`);
    return response.data;
  },

  async createBranch(tenantId: string, data: any) {
    const response = await apiClient.post(`/tenants/${tenantId}/branches`, data);
    return response.data;
  },

  async updateBranch(tenantId: string, branchId: string, data: any) {
    const response = await apiClient.put(`/tenants/${tenantId}/branches/${branchId}`, data);
    return response.data;
  },

  async deactivateBranch(tenantId: string, branchId: string) {
    const response = await apiClient.post(`/tenants/${tenantId}/branches/${branchId}/deactivate`);
    return response.data;
  },

  async activateBranch(tenantId: string, branchId: string) {
    const response = await apiClient.post(`/tenants/${tenantId}/branches/${branchId}/activate`);
    return response.data;
  },

  async syncCurriculum(tenantId: string) {
    const response = await apiClient.post(`/tenants/${tenantId}/curriculum/sync`);
    return response.data;
  },

  async updateCurricula(tenantId: string, curriculumIds: string[]) {
    const response = await apiClient.put(`/tenants/${tenantId}`, { curriculumIds });
    return response.data;
  },
};

// Platform User API
export const platformUserApi = {
  async create(data: any) {
    const response = await apiClient.post('/platform-users', data);
    return response.data;
  },

  async findAll() {
    const response = await apiClient.get('/platform-users');
    return response.data;
  },

  async findOne(id: string) {
    const response = await apiClient.get(`/platform-users/${id}`);
    return response.data;
  },

  async update(id: string, data: any) {
    const response = await apiClient.patch(`/platform-users/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/platform-users/${id}`);
    return response.data;
  },

  async activate(id: string) {
    const response = await apiClient.post(`/platform-users/${id}/activate`);
    return response.data;
  },

  async deactivate(id: string) {
    const response = await apiClient.post(`/platform-users/${id}/deactivate`);
    return response.data;
  },

  async resetPassword(id: string, newPassword: string) {
    const response = await apiClient.post(`/platform-users/${id}/reset-password`, {
      newPassword,
    });
    return response.data;
  },
};

// Billing API
export const billingApi = {
  // Billing Plans
  async createPlan(data: any) {
    const response = await apiClient.post('/billing/plans', data);
    return response.data;
  },

  async getPlans(activeOnly = false) {
    const response = await apiClient.get(`/billing/plans?activeOnly=${activeOnly}`);
    return response.data;
  },

  async getPlan(id: string) {
    const response = await apiClient.get(`/billing/plans/${id}`);
    return response.data;
  },

  async updatePlan(id: string, data: any) {
    const response = await apiClient.patch(`/billing/plans/${id}`, data);
    return response.data;
  },

  async deletePlan(id: string) {
    const response = await apiClient.delete(`/billing/plans/${id}`);
    return response.data;
  },

  // Invoices
  async generateInvoice(data: any) {
    const response = await apiClient.post('/billing/invoices', data);
    return response.data;
  },

  async getAllInvoices() {
    const response = await apiClient.get('/billing/invoices');
    return response.data;
  },

  async getInvoicesByTenant(tenantId: string) {
    const response = await apiClient.get(`/billing/invoices/tenant/${tenantId}`);
    return response.data;
  },

  async getOverdueInvoices() {
    const response = await apiClient.get('/billing/invoices/overdue');
    return response.data;
  },

  async getInvoice(id: string) {
    const response = await apiClient.get(`/billing/invoices/${id}`);
    return response.data;
  },

  async markInvoiceAsPaid(id: string) {
    const response = await apiClient.post(`/billing/invoices/${id}/mark-paid`);
    return response.data;
  },

  async recreateInvoice(id: string) {
    const response = await apiClient.post(`/billing/invoices/${id}/recreate`);
    return response.data;
  },

  // Payments
  async recordPayment(data: any) {
    const response = await apiClient.post('/billing/payments', data);
    return response.data;
  },

  async getPaymentsByTenant(tenantId: string, params?: { page?: number; limit?: number }) {
    const response = await apiClient.get(`/billing/payments/tenant/${tenantId}`, { params });
    return response.data;
  },

  async getPaymentsByInvoice(invoiceId: string) {
    const response = await apiClient.get(`/billing/payments/invoice/${invoiceId}`);
    return response.data;
  },
};

// Platform Settings API
export const platformSettingsApi = {
  async getSettings() {
    const response = await apiClient.get('/platform-settings');
    return response.data;
  },
  async updateSettings(data: {
    platformName?: string;
    platformDescription?: string;
    supportEmail?: string;
    supportPhone?: string;
    domain?: string;
    settings?: Record<string, unknown>;
  }) {
    const response = await apiClient.put('/platform-settings', data);
    return response.data;
  },
  async getMailServiceApiUrl() {
    const response = await apiClient.get('/platform-settings/email/api-url');
    return response.data;
  },
  async updateMailServiceApiUrl(apiUrl: string) {
    const response = await apiClient.put('/platform-settings/email/api-url', { apiUrl });
    return response.data;
  },
  async uploadLogo(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/platform-settings/logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  async uploadFavicon(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/platform-settings/favicon', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  async deleteLogo() {
    const response = await apiClient.delete('/platform-settings/logo');
    return response.data;
  },
  async deleteFavicon() {
    const response = await apiClient.delete('/platform-settings/favicon');
    return response.data;
  },
};

// Email API
export const emailTemplateApi = {
  async getAllTemplates() {
    const response = await apiClient.get('/email-templates');
    return response.data;
  },

  async getTemplateById(id: string) {
    const response = await apiClient.get(`/email-templates/${id}`);
    return response.data;
  },

  async getTemplateByCode(code: string) {
    const response = await apiClient.get(`/email-templates/code/${code}`);
    return response.data;
  },

  async updateTemplate(id: string, data: { subject?: string; body?: string; name?: string }) {
    const response = await apiClient.put(`/email-templates/${id}`, data);
    return response.data;
  },

  async previewTemplate(id: string, variables?: Record<string, string>) {
    const response = await apiClient.post(`/email-templates/${id}/preview`, { variables });
    return response.data;
  },

  async sendTestEmail(id: string, to: string, variables?: Record<string, string>) {
    const response = await apiClient.post(`/email-templates/${id}/test`, { to, variables });
    return response.data;
  },
};

export const emailApi = {
  // Platform email account endpoints
  async createPlatformEmailAccount(data: {
    username: string;
    password: string;
    firstName: string;
    lastName: string;
  }) {
    const response = await apiClient.post('/email/platform/account', data);
    return response.data;
  },

  async connectPlatformEmailAccount(data: {
    username: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) {
    const response = await apiClient.post('/email/platform/account/connect', data);
    return response.data;
  },
  async getPlatformEmailAccount() {
    const response = await apiClient.get('/email/platform/account');
    return response.data;
  },
  async refreshPlatformToken(data?: { username?: string; password?: string }) {
    const response = await apiClient.post('/email/platform/account/refresh-token', data || {});
    return response.data;
  },
  // Platform SMTP config endpoints
  async createPlatformSmtpConfig(data: {
    host: string;
    port: number;
    username: string;
    password: string;
    useTls: boolean;
    fromEmail?: string;
    fromName?: string;
    replyTo?: string;
    isDefault?: boolean;
    lemConfigId?: string; // Optional: link to existing Lemu config when importing
  }) {
    const response = await apiClient.post('/email/platform/smtp-config', data);
    return response.data;
  },
  async getPlatformSmtpConfigs() {
    const response = await apiClient.get('/email/platform/smtp-configs');
    return response.data;
  },
  async syncPlatformSmtpConfigsFromLemu() {
    const response = await apiClient.get('/email/platform/smtp-configs/sync');
    return response.data;
  },
  async setDefaultPlatformSmtpConfig(configId: string) {
    const response = await apiClient.patch(`/email/platform/smtp-config/${configId}/set-default`);
    return response.data;
  },
  async updatePlatformSmtpConfig(
    configId: string,
    data: {
      host?: string;
      port?: number;
      username?: string;
      password?: string;
      useTls?: boolean;
      fromEmail?: string;
      fromName?: string;
      replyTo?: string;
      isDefault?: boolean;
    }
  ) {
    const response = await apiClient.put(`/email/platform/smtp-config/${configId}`, data);
    return response.data;
  },
  async deletePlatformSmtpConfig(configId: string) {
    const response = await apiClient.delete(`/email/platform/smtp-config/${configId}`);
    return response.data;
  },
  async testPlatformSmtpConfig(configId: string, dto: { to: string }) {
    const response = await apiClient.post(`/email/platform/smtp-config/${configId}/test`, dto);
    return response.data;
  },
  // Platform Resend config endpoints — alternative to the SMTP/Lemu path
  // above, for platform-level emails only.
  async getPlatformResendConfig() {
    const response = await apiClient.get('/email/platform/resend-config');
    return response.data;
  },
  async updatePlatformResendConfig(data: {
    apiKey?: string;
    from?: string;
    fromName?: string;
    enabled?: boolean;
  }) {
    const response = await apiClient.put('/email/platform/resend-config', data);
    return response.data;
  },
  async testPlatformResendConfig(to: string) {
    const response = await apiClient.post('/email/platform/resend-config/test', { to });
    return response.data;
  },
  // Tenant email account endpoints
  async createTenantEmailAccount(
    tenantId: string,
    data: {
      username: string;
      password: string;
      firstName: string;
      lastName: string;
    }
  ) {
    const response = await apiClient.post(`/email/tenants/${tenantId}/account`, data);
    return response.data;
  },
  async getTenantEmailAccount(tenantId: string) {
    const response = await apiClient.get(`/email/tenants/${tenantId}/account`);
    return response.data;
  },
  async refreshTenantToken(tenantId: string, data?: { username?: string; password?: string }) {
    const response = await apiClient.post(
      `/email/tenants/${tenantId}/account/refresh-token`,
      data || {}
    );
    return response.data;
  },
  // Tenant SMTP config endpoints
  async createTenantSmtpConfig(
    tenantId: string,
    data: {
      host: string;
      port: number;
      username: string;
      password: string;
      useTls: boolean;
      fromEmail?: string;
      fromName?: string;
      replyTo?: string;
      isDefault?: boolean;
    }
  ) {
    const response = await apiClient.post(`/email/tenants/${tenantId}/smtp-config`, data);
    return response.data;
  },
  async getTenantSmtpConfigs(tenantId: string) {
    const response = await apiClient.get(`/email/tenants/${tenantId}/smtp-configs`);
    return response.data;
  },
  async getTenantSmtpConfig(tenantId: string, configId: string) {
    const response = await apiClient.get(`/email/tenants/${tenantId}/smtp-config/${configId}`);
    return response.data;
  },
  async updateTenantSmtpConfig(
    tenantId: string,
    configId: string,
    data: {
      host?: string;
      port?: number;
      secure?: boolean;
      username?: string;
      password?: string;
      from?: string;
      fromName?: string;
      replyTo?: string;
      enabled?: boolean;
    }
  ) {
    const response = await apiClient.put(
      `/email/tenants/${tenantId}/smtp-config/${configId}`,
      data
    );
    return response.data;
  },
  async deleteTenantSmtpConfig(tenantId: string, configId: string) {
    const response = await apiClient.delete(`/email/tenants/${tenantId}/smtp-config/${configId}`);
    return response.data;
  },
  async setDefaultTenantSmtpConfig(tenantId: string, configId: string) {
    const response = await apiClient.patch(
      `/email/tenants/${tenantId}/smtp-config/${configId}/set-default`
    );
    return response.data;
  },
  async testTenantSmtpConfig(tenantId: string, configId: string, to: string) {
    const response = await apiClient.post(
      `/email/tenants/${tenantId}/smtp-config/${configId}/test`,
      { to }
    );
    return response.data;
  },
  // Email sending
  async sendEmail(data: {
    to: string | string[];
    cc?: string | string[];
    bcc?: string | string[];
    subject: string;
    html?: string;
    text?: string;
    attachments?: Array<{ filename: string; content: string; contentType?: string }>;
    tenantId?: string;
  }) {
    const response = await apiClient.post('/email/send', data);
    return response.data;
  },
  async getEmailStatus(requestId: string, tenantId?: string) {
    const params = tenantId ? { tenantId } : {};
    const response = await apiClient.get(`/email/status/${requestId}`, { params });
    return response.data;
  },
  // Email logs
  async getEmailLogs(filters?: { status?: string; limit?: number; offset?: number }) {
    const params: any = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.limit) params.limit = filters.limit;
    if (filters?.offset) params.offset = filters.offset;
    const response = await apiClient.get('/email/logs', { params });
    return response.data;
  },
  async getTenantEmailLogs(
    tenantId: string,
    filters?: { status?: string; limit?: number; offset?: number }
  ) {
    const params: any = {};
    if (filters?.status) params.status = filters.status;
    if (filters?.limit) params.limit = filters.limit;
    if (filters?.offset) params.offset = filters.offset;
    const response = await apiClient.get(`/email/logs/tenant/${tenantId}`, { params });
    return response.data;
  },
};

// Admin API - Modules and Permissions
export const adminApi = {
  // Modules
  async getModules() {
    const response = await apiClient.get('/admin/modules');
    return response.data;
  },
  async getModule(id: string) {
    const response = await apiClient.get(`/admin/modules/${id}`);
    return response.data;
  },
  async createModule(data: {
    code: string;
    name: string;
    description?: string;
    icon?: string;
    routePath: string;
    isActive?: boolean;
  }) {
    const response = await apiClient.post('/admin/modules', data);
    return response.data;
  },
  async updateModule(
    id: string,
    data: {
      name?: string;
      description?: string;
      icon?: string;
      isActive?: boolean;
    }
  ) {
    const response = await apiClient.put(`/admin/modules/${id}`, data);
    return response.data;
  },
  async deleteModule(id: string) {
    const response = await apiClient.delete(`/admin/modules/${id}`);
    return response.data;
  },
  async activateModule(id: string) {
    const response = await apiClient.put(`/admin/modules/${id}/activate`);
    return response.data;
  },
  async deactivateModule(id: string) {
    const response = await apiClient.put(`/admin/modules/${id}/deactivate`);
    return response.data;
  },
  async assignToPricingTiers(id: string, pricingTierIds: string[]) {
    const response = await apiClient.post(`/admin/modules/${id}/pricing-tiers`, {
      pricingTierIds,
    });
    return response.data;
  },
  async assignToTenants(id: string, tenantIds: string[]) {
    const response = await apiClient.post(`/admin/modules/${id}/tenants`, {
      tenantIds,
    });
    return response.data;
  },
  async getModuleAssignments(id: string) {
    const response = await apiClient.get(`/admin/modules/${id}/assignments`);
    return response.data;
  },
  async getModulesForPricingTier(pricingTierId: string) {
    const response = await apiClient.get(`/admin/modules/pricing-tier/${pricingTierId}/modules`);
    return response.data;
  },
  // Permissions
  async getPermissions() {
    const response = await apiClient.get('/admin/permissions');
    return response.data;
  },
  async getPermission(id: string) {
    const response = await apiClient.get(`/admin/permissions/${id}`);
    return response.data;
  },
  async createPermission(data: {
    code: string;
    name: string;
    description?: string;
    category: string;
    moduleId: string;
    isActive?: boolean;
  }) {
    const response = await apiClient.post('/admin/permissions', data);
    return response.data;
  },
  async updatePermission(
    id: string,
    data: {
      name?: string;
      description?: string;
      category?: string;
      moduleId?: string;
      isActive?: boolean;
    }
  ) {
    const response = await apiClient.put(`/admin/permissions/${id}`, data);
    return response.data;
  },
  async deletePermission(id: string) {
    const response = await apiClient.delete(`/admin/permissions/${id}`);
    return response.data;
  },
  async activatePermission(id: string) {
    const response = await apiClient.put(`/admin/permissions/${id}/activate`);
    return response.data;
  },
  async deactivatePermission(id: string) {
    const response = await apiClient.put(`/admin/permissions/${id}/deactivate`);
    return response.data;
  },
  async getPermissionsByModule(moduleId: string) {
    const response = await apiClient.get(`/admin/permissions/module/${moduleId}`);
    return response.data;
  },
};

// Curriculum API
export const platformAcademicYearsApi = {
  async getAll(curriculumId?: string) {
    const response = await apiClient.get('/platform/academic-years', {
      params: curriculumId ? { curriculumId } : {},
    });
    return response.data;
  },

  async getById(id: string) {
    const response = await apiClient.get(`/platform/academic-years/${id}`);
    return response.data;
  },

  async create(data: {
    curriculumId: string;
    name: string;
    startDate: string;
    endDate: string;
    description?: string;
    terms?: Array<{
      name: string;
      sequence: number;
      openingDate: string;
      closingDate: string;
      durationWeeks?: number;
      description?: string;
    }>;
    holidays?: Array<{
      name: string;
      breakType: string;
      startDate: string;
      endDate: string;
      durationDays?: number;
      description?: string;
    }>;
    assessments?: Array<{
      name: string;
      code?: string;
      startDate: string;
      endDate: string;
      durationDays?: number;
      description?: string;
    }>;
  }) {
    const response = await apiClient.post('/platform/academic-years', data);
    return response.data;
  },

  async update(
    id: string,
    data: {
      name?: string;
      startDate?: string;
      endDate?: string;
      description?: string;
      isActive?: boolean;
      terms?: Array<{
        name: string;
        sequence: number;
        openingDate: string;
        closingDate: string;
        durationWeeks?: number;
        description?: string;
      }>;
      holidays?: Array<{
        name: string;
        breakType: string;
        startDate: string;
        endDate: string;
        durationDays?: number;
        description?: string;
      }>;
      assessments?: Array<{
        name: string;
        code?: string;
        startDate: string;
        endDate: string;
        durationDays?: number;
        description?: string;
      }>;
    }
  ) {
    const response = await apiClient.patch(`/platform/academic-years/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await apiClient.delete(`/platform/academic-years/${id}`);
    return response.data;
  },
};

export const curriculumApi = {
  async getAllCurricula(filters?: {
    isActive?: boolean;
    isSystem?: boolean;
    country?: string;
    search?: string;
  }) {
    const params: any = {};
    if (filters?.isActive !== undefined) params.isActive = filters.isActive;
    if (filters?.isSystem !== undefined) params.isSystem = filters.isSystem;
    if (filters?.country) params.country = filters.country;
    if (filters?.search) params.search = filters.search;
    const response = await apiClient.get('/admin/curricula', { params });
    return response.data;
  },

  async getCurriculumById(id: string) {
    const response = await apiClient.get(`/admin/curricula/${id}`);
    return response.data;
  },

  async getCurriculumVersions(id: string) {
    const response = await apiClient.get(`/admin/curricula/${id}/versions`);
    return response.data;
  },

  async compareVersions(id: string, version1: string, version2: string) {
    const response = await apiClient.get(`/admin/curricula/${id}/compare`, {
      params: { version1, version2 },
    });
    return response.data;
  },

  async getTenantsUsingCurriculum(id: string) {
    const response = await apiClient.get(`/admin/curricula/${id}/tenants`);
    return response.data;
  },
};

// Export the apiClient for other API calls
export default apiClient;
